// -------------------------------------------------------------------------------
// Libraries
// -------------------------------------------------------------------------------
import ReactPlayer from 'react-player';
import { FormattedTime } from 'react-player-controls';
import { useCallback, useRef, useState } from 'react';

// -------------------------------------------------------------------------------
// Store
// -------------------------------------------------------------------------------
import { Paragraph12, Paragraph13 } from '../../theme/theme-provider/typography';

// -------------------------------------------------------------------------------
// Components
// -------------------------------------------------------------------------------
import { PlayIcon, PauseIcon, TrackProgressBar, VolumeOffIcon, VolumeOnIcon } from '../common';
import { StepForwardOutlined, FastForwardOutlined, MenuOutlined, CloseSquareOutlined } from '@ant-design/icons';
import { Tracklist } from '../tracklist';

// -------------------------------------------------------------------------------
// Styles and Utils
// -------------------------------------------------------------------------------
import { StyledMusicPlayer } from './style';

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open('resources', 1);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      db.createObjectStore('images', { keyPath: 'url' });
      db.createObjectStore('mp3', { keyPath: 'url' });
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = (error) => reject(error);
  });
};

function getCachedMp3(url) {
  return openDB().then((db) => {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['mp3'], 'readonly');
      const store = transaction.objectStore('mp3');
      const request = store.get(url);

      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.mediaData : null);
      };

      request.onerror = (error) => reject(error);
    });
  });
}

function timeStringToSeconds(timeString) {
  const [hours, minutes, seconds] = timeString.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
}

// -------------------------------------------------------------------------------
// Component
// -------------------------------------------------------------------------------
export function MusicPlayer({ track, firstLoad}) {
  const playerRef = useRef(null);
  const [isPlaying, setPlaying] = useState(true);
  const [isMuted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [played, setPlayed] = useState(0); 

  const handleProgress = (progressObj) => {
    setPlayed(Math.ceil(progressObj.playedSeconds));
  };

  const onChangePlayingStatus = useCallback(() => {
    setPlaying(!isPlaying);
  }, [isPlaying]);

  const onVolumeBarChange = useCallback((newPercentValue) => {
    setVolume(newPercentValue);
    setMuted(false);
  }, []);

  const onVolumeStatusChange = useCallback(() => {
    setMuted(!isMuted);
  }, [isMuted]);

  const onNextTrack = useCallback(() => {
    let i=0;
    do {
      i++;
    } while (timeStringToSeconds(window.mixes[window.currentlyplayingindex].Tracklists[i].time) < played) 
    playerRef.current.seekTo(parseFloat(timeStringToSeconds(window.mixes[window.currentlyplayingindex].Tracklists[i].time)), 'seconds');
  }, [played]);

  const openDialogTracklist = () => {
    const dialog = document.getElementById('tracklistDialog');
    dialog.showModal();
  }
  const closeTracklist = () => {
    const dialog = document.getElementById('tracklistDialog');
    dialog.close();
  }
  const setNextMix = useCallback(() => {
    const currentIndex = window.currentlyplayingindex;
    setPlayed(0);
    if(window.mixes[currentIndex].isCached === 2) {
      window.mixes[currentIndex].blobUrl = null;
    }
    let nextMixIndex = (currentIndex + 1) % window.mixes.length;
    while (window.mixes[nextMixIndex].isCached === 0) {
      nextMixIndex = (nextMixIndex + 1) % window.mixes.length;
    }
    const mixElement = document.getElementById('mix_' + nextMixIndex);
    if (window.currentlyplaying !== window.mixes[nextMixIndex].id) {
      if (window.mixes[nextMixIndex].isCached === 1) {
          mixElement.click();
      }
      if (window.mixes[nextMixIndex].isCached ===2) {
        // Fetch and set the cached MP3 data from IndexedDB
        if ( window.mixes[nextMixIndex].blobUrl === null) {
          getCachedMp3(window.mixes[nextMixIndex].default_url).then((cachedMp3Data) => {
            if (cachedMp3Data !== null) {
              // Set the cached MP3 data as the audio source
              window.mixes[nextMixIndex].blobUrl = cachedMp3Data;
              mixElement.click();
            } else {
              console.error("No cachedMp3Data found.");
            }
          });
        } else {
          mixElement.click();
        }
      }
    } 
    
  }, []);


  return (
    <StyledMusicPlayer>
      <div className="fr-music-player">
        <ReactPlayer
          ref={playerRef}
          progressInterval={1000}
          volume={volume}
          playing={isPlaying}
          muted={isMuted}
          autoPlay={true}
          url={track.blobUrl}
          className="fr-music-player__player"
          width="100%"
          height="100%"
          onDuration={(d) => setDuration(d)}
          onProgress={handleProgress}
          onEnded = {() => setNextMix()}
          onError={(e) => {
            console.error('Error message:', e.target.error);
            if (window.firstLoad !== true) {
              window.firstLoad = false;
              setNextMix();
            }
          }}
        />
        {!firstLoad && (
          <div className="fr-music-player__controls">
            <div className="fr-music-player__controls__track-info">
              <div className="fr-music-player__controls__track-info__image">
                <img src={track.background_image} alt="dj" />
              </div>
              <div className="fr-music-player__controls__track-info__title">
                {track && (
                  <>
                    <Paragraph12>{track.title}</Paragraph12>
                    <Paragraph13>By {track.dj_name}</Paragraph13>
                  </>
                )}
              </div>
            </div>
            <div className="fr-music-player__controls__player">
              <div className="fr-music-player__controls__player__play">
              <span className="fr-music-player__controls__player__play__tracklist-button" onClick={openDialogTracklist}>
                  <MenuOutlined  style={{ fontSize: '40px' }} />
                </span>
                <span className="fr-music-player__controls__player__play__play-button" onClick={onChangePlayingStatus}>
                  {isPlaying && <PauseIcon />}
                  {!isPlaying && <PlayIcon />}
                </span>
                <span className="fr-music-player__controls__player__play__next-button" onClick={onNextTrack}>
                  <StepForwardOutlined  style={{ fontSize: '40px' }} />
                </span>
                <span className="fr-music-player__controls__player__play__next-button" onClick={setNextMix}>
                  <FastForwardOutlined  style={{ fontSize: '40px' }} />
                </span>
              </div>
              <div className="fr-music-player__controls__player__progress">
                <FormattedTime numSeconds={played} />
                <TrackProgressBar
                  value={played}
                  max={Math.ceil(duration)}
                  transitionDuration={0.1}
                />
                <FormattedTime numSeconds={duration} />
              </div>
            </div>
            <div className="fr-music-player__controls__volume">
              <span className="fr-music-player__controls__volume__action" onClick={onVolumeStatusChange}>
                {(isMuted || volume === 0) && <VolumeOffIcon />}
                {!isMuted && volume > 0 && <VolumeOnIcon />}
              </span>
              <TrackProgressBar
                value={isMuted ? 0 : volume * 100}
                max={100}
                transitionDuration={0.1}
                onChange={onVolumeBarChange}
              />
            </div>
          </div>
        )}
      </div>
      <dialog id="tracklistDialog" className="modal" style={{ backgroundColor: '#000000', color: '#ffffff', minWidth: '800px'}}>
      <span onClick={closeTracklist}>
        
      <CloseSquareOutlined  style={{ cursor:'pointer', fontSize: '24px', color:'#ffffff',  float: 'right' }} />
      </span>
      
      <Tracklist tracklist={track.Tracklists} played={played}>
        </Tracklist>
    </dialog>
    </StyledMusicPlayer>
  );
}

export default MusicPlayer;
